/**
 * provider.tsx — React surface for the platform descriptor.
 *
 * `<PlatformProvider>` runs detection once and exposes it via context. Consumers
 * use `usePlatform()` for the full descriptor and `useCapability("…")` for a
 * single flag. It also stamps `data-omni-host` / `data-form-factor` on the
 * document root and publishes safe-area insets as CSS vars, so Tailwind variants
 * and `env(safe-area-inset-*)` fallbacks work without per-component JS.
 *
 * See Docs/architecture/26-electron-desktop-and-mobile-architecture.md §2.
 */

import * as React from "react";
import { detectPlatform, WEB_DEFAULT, type PlatformDescriptor } from "./detect";
import type { CapabilityFlags } from "./capabilities";

const PlatformCtx = React.createContext<PlatformDescriptor>(WEB_DEFAULT);

export interface PlatformProviderProps {
  /** Override detection (tests, Storybook). */
  value?: PlatformDescriptor;
  children: React.ReactNode;
}

export function PlatformProvider({ value, children }: PlatformProviderProps): React.ReactElement {
  // Detect lazily once; re-detect on viewport resize (form factor can change on
  // foldables / window resize / browser-devtools device emulation).
  const [descriptor, setDescriptor] = React.useState<PlatformDescriptor>(
    () => value ?? detectPlatform(),
  );

  React.useEffect(() => {
    if (value) {
      setDescriptor(value);
      return;
    }
    const onResize = () => setDescriptor(detectPlatform());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [value]);

  // Reflect the descriptor onto the document root for CSS / Tailwind variants.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.setAttribute("data-omni-host", descriptor.host);
    root.setAttribute("data-form-factor", descriptor.formFactor);
    root.style.setProperty("--safe-top", `${descriptor.safeArea.top}px`);
    root.style.setProperty("--safe-bottom", `${descriptor.safeArea.bottom}px`);
    root.style.setProperty("--safe-left", `${descriptor.safeArea.left}px`);
    root.style.setProperty("--safe-right", `${descriptor.safeArea.right}px`);
  }, [descriptor]);

  return <PlatformCtx.Provider value={descriptor}>{children}</PlatformCtx.Provider>;
}

export function usePlatform(): PlatformDescriptor {
  return React.useContext(PlatformCtx);
}

export function useCapability<K extends keyof CapabilityFlags>(flag: K): boolean {
  return React.useContext(PlatformCtx).capabilities[flag];
}

/** Convenience: true when running inside Electron. */
export function useIsDesktop(): boolean {
  return usePlatform().host === "electron";
}

/** Convenience: true when running inside a Capacitor native WebView. */
export function useIsMobile(): boolean {
  const host = usePlatform().host;
  return host === "ios" || host === "android";
}
